<template>
  <!-- Payment Process Modal -->
  <div v-if="show" class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header bg-success text-white">
          <h5 class="modal-title">
            <i class="bx bx-money me-2"></i>
            Process Settlement Payments
          </h5>
          <button type="button" class="btn-close btn-close-white" @click="$emit('close')"></button>
        </div>

        <!-- Modal Body -->
        <div class="modal-body">
          <div class="alert alert-info">
            <h6 class="alert-heading"><i class="bx bx-info-circle me-2"></i>Batch Payment Processing</h6>
            <p class="mb-0">You are about to process payment for <strong>{{ selectedBatches.length }}</strong> settlement batches
              with a total amount of <strong>ETB {{ formatAmount(totalAmount) }}</strong>.</p>
          </div>

          <!-- Selected Batches Summary -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">Selected Settlement Batches</h6>
            </div>
            <div class="card-body">
              <div class="table-responsive" style="max-height: 300px; overflow-y: auto;">
                <table class="table table-sm mb-0">
                  <thead class="table-light sticky-top">
                  <tr>
                    <th>Batch Number</th>
                    <th>Hospital</th>
                    <th>Claims</th>
                    <th class="text-end">Amount</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="batch in selectedBatches" :key="batch.batch_id">
                    <td>{{ batch.batch_number }}</td>
                    <td>{{ batch.hospital_name }}</td>
                    <td>{{ batch.total_claims }}</td>
                    <td class="text-end fw-bold">ETB {{ formatAmount(batch.total_amount) }}</td>
                  </tr>
                  </tbody>
                  <tfoot class="table-light">
                  <tr>
                    <td colspan="3" class="text-end fw-bold">Total Amount:</td>
                    <td class="text-end fw-bold text-primary">ETB {{ formatAmount(totalAmount) }}</td>
                  </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          <!-- Payment Form -->
          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label">Bank Reference Number <span class="text-danger">*</span></label>
                <input v-model="paymentData.bankReference" type="text" class="form-control"
                       placeholder="Enter bank reference number">
                <small class="text-muted">This reference will be used for all selected batches</small>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label">Payment Date <span class="text-danger">*</span></label>
                <input v-model="paymentData.paymentDate" type="date" class="form-control">
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Payment Method</label>
            <select v-model="paymentData.paymentMethod" class="form-select">
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="electronic">Electronic Transfer</option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">Notes (Optional)</label>
            <textarea v-model="paymentData.notes" class="form-control" rows="3"
                      placeholder="Add any notes about this payment batch..."></textarea>
          </div>

          <!-- Confirmation -->
          <div class="card bg-light">
            <div class="card-body">
              <h6 class="card-title">Payment Confirmation</h6>
              <div class="form-check">
                <input v-model="paymentData.confirmed" class="form-check-input" type="checkbox" id="confirmCheck">
                <label class="form-check-label" for="confirmCheck">
                  I confirm that the payment details are correct and the funds have been transferred to the respective hospital accounts.
                </label>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="modal-footer bg-light">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            Cancel
          </button>
          <button @click="processPayment" class="btn btn-success" :disabled="!canProcess || processing">
            <span v-if="processing" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bx bx-check-circle me-1"></i>
            Process Payment
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PaymentProcessModal",
  props: {
    batches: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      processing: false,
      paymentData: {
        bankReference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'bank_transfer',
        notes: '',
        confirmed: false
      }
    };
  },

  computed: {
    show() {
      return this.batches && this.batches.length > 0;
    },

    selectedBatches() {
      return this.batches || [];
    },

    totalAmount() {
      return this.selectedBatches.reduce((sum, batch) => sum + parseFloat(batch.total_amount || 0), 0);
    },

    canProcess() {
      return this.paymentData.bankReference &&
          this.paymentData.paymentDate &&
          this.paymentData.confirmed &&
          !this.processing;
    }
  },

  methods: {
    async processPayment() {
      if (!this.canProcess) return;

      this.processing = true;

      const data = {
        batchIds: this.selectedBatches.map(b => b.batch_id),
        ...this.paymentData
      };

      this.$emit('process', data);

      // Reset form after submission
      setTimeout(() => {
        this.processing = false;
        this.resetForm();
      }, 1000);
    },

    resetForm() {
      this.paymentData = {
        bankReference: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'bank_transfer',
        notes: '',
        confirmed: false
      };
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(amount || 0);
    }
  }
};
</script>

<style scoped>
.modal.show {
  display: block !important;
}

.btn-close-white {
  filter: brightness(0) invert(1);
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

.sticky-top {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #f8f9fa;
}
</style>